import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Button, Image } from "react-bootstrap";
import { CreditCard, ThumbsUp, Shield, Clock, MapPin, Users, AlertCircle } from "lucide-react";
import Menu from "../sections/Menu";
import Footer from "../sections/Footer";
import Partners from "../sections/Partners";
import FAQ from "../sections/FAQ";
import AnimatedCounter from "../components/AnimatedCounter";
import heroInzynierskie from "../images/hero_inzynierskie.png";
import mockupInz from "../images/mockup_inz.jpeg";
import mockupInz1 from "../images/mockup_inz1.jpeg";
import deklarowanie_vid from "../videos/deklarowanie_vid.mp4";
import rejestrowanie_vid from "../videos/rejestrowanie_vid.mp4";
import podsumowanie_dnia_vid from "../videos/podsumowanie_dnia_vid.mp4";
import rentownosc_vid from "../videos/rentownosc_vid.mp4";
import eksporty_vid from "../videos/eksporty_vid.mp4";
import PageMeta from "../seo/PageMeta";
import {
  buildFaqStructuredData,
  buildServiceStructuredData,
  buildVideoObjectStructuredData,
} from "../seo/structuredData";

function InzynieriaBudownictwo() {
  const [content, setContent] = useState(null);
  useEffect(() => {
    // Load main content
    fetch("/data/content.json")
      .then((res) => res.json())
      .then((data) => {
        // Load EGodziny-specific FAQ
        fetch("/data/egodziny-content.json")
          .then((res) => res.json())
          .then((egodzinyData) => {
            setContent({
              ...data,
              faq: egodzinyData.faq
            });
          })
          .catch((error) => {
            console.error("Error loading EGodziny FAQ:", error);
            setContent(data);
          });
      })
      .catch((error) => console.error("Error loading JSON:", error));
  }, []);

  useEffect(() => {
    const words = ["pracownik", "brygadzista"];
    let currentWordIndex = 0;

    const typewriterAnimation = () => {
      const element = document.getElementById("typing-text");
      if (!element) return;

      const currentWord = words[currentWordIndex];
      let currentText = "";
      let letterIndex = 0;

      // Clear element first
      element.textContent = "";

      const typeInterval = setInterval(() => {
        if (letterIndex < currentWord.length) {
          currentText += currentWord[letterIndex];
          element.textContent = currentText;
          letterIndex++;
        } else {
          clearInterval(typeInterval);

          // Wait before starting to delete
          setTimeout(() => {
            const deleteInterval = setInterval(() => {
              if (currentText.length > 0) {
                currentText = currentText.slice(0, -1);
                element.textContent = currentText;
              } else {
                clearInterval(deleteInterval);
                currentWordIndex = (currentWordIndex + 1) % words.length;

                // Wait before typing next word
                setTimeout(() => {
                  typewriterAnimation();
                }, 200);
              }
            }, 100); // Delete speed
          }, 1500); // Pause before deleting
        }
      }, 150); // Typing speed
    };

    // Start animation after content loads
    const startTimeout = setTimeout(() => {
      typewriterAnimation();
    }, 500);

    return () => clearTimeout(startTimeout);
  }, [content]);

  const pageStructuredData = useMemo(
    () =>
      [
        buildFaqStructuredData(content, "/inzynieria-i-budownictwo"),
        buildServiceStructuredData({
          path: "/inzynieria-i-budownictwo",
          name: "System RCP dla inżynierii i budownictwa",
          description:
            "eGodziny dla firm budowlanych i inżynierskich: mobilna ewidencja czasu pracy, rozliczanie projektów i raportowanie.",
          serviceType: "System ewidencji czasu pracy dla firm terenowych",
          audience: "Firmy inżynieryjne i budowlane",
        }),
        buildVideoObjectStructuredData({
          path: "/inzynieria-i-budownictwo",
          name: "Deklarowanie czasu pracy",
          description: "Prezentacja funkcji deklarowania czasu pracy w eGodziny.",
          contentUrl: deklarowanie_vid,
        }),
        buildVideoObjectStructuredData({
          path: "/inzynieria-i-budownictwo",
          name: "Rejestrowanie czasu pracy",
          description: "Prezentacja rejestrowania czasu pracy w aplikacji eGodziny.",
          contentUrl: rejestrowanie_vid,
        }),
        buildVideoObjectStructuredData({
          path: "/inzynieria-i-budownictwo",
          name: "Rentowność projektów",
          description: "Analiza rentowności projektów w systemie eGodziny.",
          contentUrl: rentownosc_vid,
        }),
        buildVideoObjectStructuredData({
          path: "/inzynieria-i-budownictwo",
          name: "Podsumowanie dnia",
          description: "Podsumowanie dnia pracy i raportowanie w eGodziny.",
          contentUrl: podsumowanie_dnia_vid,
        }),
        buildVideoObjectStructuredData({
          path: "/inzynieria-i-budownictwo",
          name: "Eksport danych",
          description: "Eksport danych do rozliczeń w systemie eGodziny.",
          contentUrl: eksporty_vid,
        }),
      ].filter(Boolean),
    [content]
  );

  if (!content) return <div></div>;

  return (
    <>
      <PageMeta
        path="/inzynieria-i-budownictwo"
        structuredData={pageStructuredData}
      />
      <Menu />

      {/* Hero Section */}
      <Container
        className="hero align-content-center mb-5"
        style={{ paddingTop: "80px", paddingBottom: "80px" }}
      >
        <Row className="align-items-center">
          <Col xs={12} md={6} className="text-start">
            {/* Small badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#EEF3FF',
              padding: '8px 16px',
              borderRadius: '24px',
              marginBottom: '24px',
              border: '1px solid #C7D9FF'
            }}>
              <Shield size={16} style={{ color: "#407EF9" }} />
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#407EF9'
              }}>
                Dedykowane dla firm budowlanych i inżynierskich
              </span>
            </div>

            {/* Main headline */}
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '800',
              color: '#1f2937',
              marginBottom: '24px',
              lineHeight: '1.15',
              letterSpacing: '-0.02em',
              textAlign: 'left'
            }}>
              Zero papieru.<br />
              Zero rozbieżności.<br />
              <span style={{ color: '#407EF9' }}>Pełna kontrola</span> nad czasem pracy.
            </h1>

            {/* Subheadline */}
            <p style={{
              fontSize: 'clamp(1.1rem, 2vw, 1.25rem)',
              color: '#4b5563',
              marginBottom: '32px',
              lineHeight: '1.6',
              fontWeight: '500',
              textAlign: 'left',
              maxWidth: '620px'
            }}>
              System i aplikacja dla firm wykonawczych. Pracownicy raportują czas z telefonu, a Ty masz gotowe rozliczenia bez przepisywania papierowych list.
            </p>

            {/* Key benefits with icons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '36px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: '#EEF3FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Clock size={16} style={{ color: "#407EF9" }} />
                </div>
                <span style={{
                  fontSize: '16px',
                  color: '#374151',
                  fontWeight: '500'
                }}>
                  Oszczędzasz 35 godzin miesięcznie
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: '#EEF3FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <MapPin size={16} style={{ color: "#407EF9" }} />
                </div>
                <span style={{
                  fontSize: '16px',
                  color: '#374151',
                  fontWeight: '500'
                }}>
                  GPS weryfikacja obecności na budowie
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: '#EEF3FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Users size={16} style={{ color: "#407EF9" }} />
                </div>
                <span style={{
                  fontSize: '16px',
                  color: '#374151',
                  fontWeight: '500'
                }}>
                  Zarządzanie brygadami i podwykonawcami
                </span>
              </div>
            </div>

            {/* Mobile stacked visuals */}
            <div className="d-md-none mb-4">
              <div className="inz-hero-stack inz-hero-stack-mobile">
                <Image 
                  src={heroInzynierskie} 
                  alt="Tło budowlane"
                  className="inz-hero-bg"
                />
                <Image 
                  src={mockupInz}
                  alt="eGodziny na telefonie"
                  className="inz-hero-mock inz-hero-mock-left"
                />
                <Image 
                  src={mockupInz1}
                  alt="eGodziny interfejs"
                  className="inz-hero-mock inz-hero-mock-right"
                />
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="inz-hero-buttons" style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '32px',
              flexWrap: 'wrap'
            }}>
              <Button
                className="button-default"
                style={{
                  fontSize: '16px',
                  padding: '14px 32px',
                  height: 'auto',
                  width: 'auto',
                  minWidth: '180px'
                }}
                onClick={() =>
                  window.open(
                    "/register",
                    "_self",
                  )
                }
              >
                Wypróbuj za darmo
              </Button>

              <Button
                variant="outline-dark"
                style={{
                  fontSize: '16px',
                  padding: '14px 32px',
                  height: 'auto',
                  width: 'auto',
                  minWidth: '180px',
                  borderRadius: '10px',
                  borderWidth: '2px',
                  fontWeight: '600'
                }}
                onClick={() => {window.open('https://calendly.com/krzysztof-warda-egodziny/30min', '_blank');}}
              >
                Umów demo
              </Button>
            </div>

            {/* Trust badges */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <CreditCard size={16} style={{ color: "#407EF9" }} />
                <span style={{
                  fontSize: '13px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  Bez karty
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <ThumbsUp size={16} style={{ color: "#407EF9" }} />
                <span style={{
                  fontSize: '13px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  Łatwe w użyciu
                </span>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <Shield size={16} style={{ color: "#407EF9" }} />
                <span style={{
                  fontSize: '13px',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  Zgodny z RODO
                </span>
              </div>
            </div>
          </Col>

          <Col xs={12} md={6} className="d-none d-md-block text-center">
            <div className="inz-hero-stack">
              {/* Background image */}
              <Image 
                src={heroInzynierskie} 
                alt="Tło budowlane"
                className="inz-hero-bg"
              />

              {/* Mockup 1 - positioned on the left */}
              <Image 
                src={mockupInz}
                alt="eGodziny na telefonie"
                className="inz-hero-mock inz-hero-mock-left"
              />

              {/* Mockup 2 - positioned on the right, slightly forward */}
              <Image 
                src={mockupInz1}
                alt="eGodziny interfejs"
                className="inz-hero-mock inz-hero-mock-right"
              />
            </div>
          </Col>
        </Row>
      </Container>

      <div style={{height: "40px"}}></div>

      {/* Partners Section */}
      <Partners />

      {/* Problem & Benefits Section */}
      <Container fluid style={{ backgroundColor: "#F9FAFB", padding: "80px 20px" }}>
        <Container style={{ maxWidth: "800px" }}>
          {/* Headline */}
          <div className="text-center" style={{ marginBottom: "48px" }}>
            <div style={{
              display: "inline-block",
              backgroundColor: "#EEF3FF",
              padding: "8px 20px",
              borderRadius: "20px",
              marginBottom: "20px",
              border: "1px solid #C7D9FF"
            }}>
              <span style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#407EF9",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                Dlaczego eGodziny
              </span>
            </div>
            <h2 style={{
              fontSize: "clamp(1.8rem, 4vw, 2.5rem)",
              fontWeight: "800",
              color: "#1f2937",
              lineHeight: "1.15",
              letterSpacing: "-0.02em",
              marginBottom: "16px"
            }}>
              Wiesz ile zarobiłeś<br /><span style={{ color: "#407EF9" }}>Na każdym kontrakcie.</span>
            </h2>
            <p style={{
              fontSize: "1.1rem",
              color: "#6b7280",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.7"
            }}>
              Koniec z liczeniem z kartek i WhatsAppa. Wszystkie godziny pracy rejestrowane z telefonu - automatycznie przypisane do budowy. W każdej chwili wiesz, czy kontrakt się opłaca.
            </p>
          </div>

          {/* Problem box */}
          <div style={{
            backgroundColor: "#ffffff",
            borderRadius: "20px",
            padding: "32px",
            marginBottom: "32px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
          }}>
            <p style={{
              fontSize: "12px",
              fontWeight: "600",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#9ca3af",
              marginBottom: "20px"
            }}>
              Brzmi znajomo?
            </p>
            {[
              { text: <>Godziny pracy zbierane na kartkach - <strong style={{ color: "#407EF9" }}>bez podziału na konkretne budowy</strong></> },
              { text: <>Nie ma czasu podliczyć, <strong style={{ color: "#407EF9" }}>który kontrakt zajął ile godzin</strong></> },
              { text: <>Nie znamy kosztów robocizny, <strong style={{ color: "#407EF9" }}>nie wiemy na którym kontrakcie zarobiliśmy</strong></> }
            ].map((item, idx) => (
              <div key={idx} style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                padding: "12px 0",
                borderBottom: idx < 2 ? "1px solid #f3f4f6" : "none"
              }}>
                <AlertCircle size={20} style={{ color: "#ef4444", flexShrink: 0, marginTop: "2px" }} />
                <p style={{
                  fontSize: "15px",
                  lineHeight: "1.6",
                  color: "#6b7280",
                  margin: 0
                }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          {/* Insight box */}
          <div style={{
            backgroundColor: "#EEF3FF",
            padding: "24px",
            borderRadius: "20px",
            borderLeft: "4px solid #407EF9",
            marginBottom: "48px"
          }}>
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px"
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span style={{ fontSize: "1.05rem", color: "#407EF9", fontWeight: "bold", marginTop: "2px" }}>•</span>
                <span style={{ fontSize: "1.05rem", color: "#1e3a5f", lineHeight: "1.6" }}>
                  W eGodzinach każda godzina przypisana jest do konkretnego kontraktu
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <span style={{ fontSize: "1.05rem", color: "#407EF9", fontWeight: "bold", marginTop: "2px" }}>•</span>
                <span style={{ fontSize: "1.05rem", color: "#1e3a5f", lineHeight: "1.6" }}>
                  Wiesz ile kosztowała każda budowa i możesz lepiej wyceniać kolejne zlecenia
                </span>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <h3 style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            color: "#1f2937",
            marginBottom: "20px",
            textAlign: "center"
          }}>
            Co dostajesz od razu
          </h3>
          <Row className="g-3 justify-content-center" style={{ marginBottom: "48px" }}>
            {[
              { num: "01", label: "Policzone godziny każdego pracownika - z podziałem na weekendy, święta i nadgodziny" },
              { num: "02", label: "Policzone godziny na każdej budowie - z możliwością podziału na etapy prac lub czynności" },
              { num: "03", label: "Automatyczne naliczanie pensji - wystarczy wpisać stawkę godzinową i gotowe" },
              { num: "04", label: "Wiedza, który kontrakt się opłacał i jak wyceniać kolejne" }
            ].map((benefit, idx) => (
              <Col key={idx} xs={12} sm={6} md={3}>
                <div style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "20px",
                  padding: "20px",
                  height: "100%",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#407EF9";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(64, 126, 249, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}>
                  <div style={{
                    fontSize: "2rem",
                    fontWeight: "800",
                    color: "#407EF9",
                    lineHeight: 1,
                    marginBottom: "8px"
                  }}>
                    {benefit.num}
                  </div>
                  <div style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    lineHeight: "1.5"
                  }}>
                    {benefit.label}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* CTA block */}
          <div style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "20px",
            padding: "48px 32px",
            textAlign: "center"
          }}>
            <h3 style={{
              fontSize: "1.4rem",
              fontWeight: "700",
              color: "#1f2937",
              marginBottom: "12px"
            }}>
              Policz wreszcie, ile zarabiasz na każdej budowie
            </h3>
            <p style={{
              color: "#6b7280",
              fontSize: "0.95rem",
              marginBottom: "24px",
              lineHeight: "1.6"
            }}>
              Dołącz do dziesiątek firm budowlanych, które zamieniły kartki i WhatsAppa na rzetelne dane z telefonu.
            </p>
            <Button
              className="button-default"
              style={{ padding: "14px 32px", fontSize: "1rem", width: "auto" }}
              onClick={() => window.open("/register", "_self")}
            >
              Wypróbuj eGodziny za darmo
            </Button>
          </div>
        </Container>
      </Container>

      {/* Registration Methods Section */}
      <Container fluid style={{ backgroundColor: '#ffffff', padding: '80px 20px', marginTop: "40px" }}>
        <Container>
          <Row className='mb-5'>
            <Col className='text-center'>
              <h2
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                Różne sposoby rejestracji czasu pracy
              </h2>
              <p
                style={{
                  fontSize: "1.1rem",
                  color: "#6b7280",
                  marginBottom: "40px",
                  textAlign: "center",
                  maxWidth: "800px",
                  margin: "0 auto 40px auto",
                  lineHeight: "1.6",
                }}
              >
                Firmy różnią się sposobem pracy, poziomem zaufania i potrzebą kontroli. W eGodzinach możesz wybrać metodę rejestracji czasu, która sprawdzi się najlepiej.
              </p>
            </Col>
          </Row>

          <Row className='justify-content-center'>
            {[
              {
                icon: 'Clock',
                title: "Deklarowanie czasu pracy",
                forWhom: "Dla zaufanych zespołów i pracy przy projektach",
                description: "Pracownicy wpisują liczbę przepracowanych godzin oraz projekt. Najprostszy sposób na zastąpienie papierowych list i szybkie rozliczenia."
              },
              {
                icon: 'MapPin',
                title: "Start–Stop + GPS",
                forWhom: "Dla firm wymagających pełnej kontroli obecności",
                description: "Pracownik rozpoczyna i kończy pracę jednym kliknięciem, a system potwierdza jego lokalizację."
              },
              {
                icon: 'Users',
                title: "Brygadzista rejestruje czas ekipy",
                forWhom: "Dla niektórych zespołów pracujących w brygadach",
                description: "Lider zespołu wprowadza czas pracy całej brygady na koniec dnia. Minimalna liczba kliknięć."
              }
            ].map((method, index) => {
              const IconComponent = method.icon === 'Clock' ? Clock : method.icon === 'MapPin' ? MapPin : Users;
              return (
                <Col key={index} xs={12} md={6} lg={4} className='mb-4'>
                  <div className='registration-method-card'>
                    <div className='method-icon-wrapper'>
                      <IconComponent
                        size={40}
                        strokeWidth={1.5}
                        style={{ color: '#407EF9' }}
                      />
                    </div>

                    <h4 style={{
                      fontSize: '1.375rem',
                      fontWeight: '600',
                      color: '#262A33',
                      marginBottom: '12px',
                      marginTop: '20px'
                    }}>
                      {method.title}
                    </h4>

                    <p style={{
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      color: '#407EF9',
                      marginBottom: '16px'
                    }}>
                      {method.forWhom}
                    </p>

                    <p style={{
                      fontSize: '1rem',
                      color: '#6B7280',
                      lineHeight: '1.6',
                      marginBottom: '0'
                    }}>
                      {method.description}
                    </p>
                  </div>
                </Col>
              );
            })}
          </Row>

          <Row className='mt-4 pt-2' style={{ paddingBottom: '20px' }}>
            <Col className='text-center'>
              <div>
                <p className="text-center" style={{
                  fontSize: '0.95rem',
                  color: '#6b7280',
                  marginBottom: '16px',
                  fontWeight: '500'
                }}>
                  Nie jesteś pewien, która metoda sprawdzi się najlepiej?<br />
                  Zdzwońmy się. Doradzimy na podstawie doświadczeń z podobnych firm.
                </p>
                <Button 
                  onClick={() => {window.open('https://calendly.com/krzysztof-warda-egodziny/30min', '_blank');}}
                  className="button-default"
                  style={{
                    backgroundColor: '#407EF9',
                    color: 'white',
                    border: '2px solid #407EF9',
                    borderRadius: '11px',
                    padding: '13.2px 20px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.backgroundColor = '#3366CC';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.backgroundColor = '#407EF9';
                  }}
                >
                  Umów demo
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </Container>

      {/* Workflow Section Header */}
      <Container className="py-5" style={{ marginTop: "60px" }}>
        <Row className="mb-5">
          <Col className="text-center">
            <div style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)",
              borderRadius: "16px",
              padding: "48px 24px",
              border: "1px solid rgba(0, 0, 0, 0.08)"
            }}>
                <div style={{
                  display: "inline-block",
                  backgroundColor: "#EEF3FF",
                  padding: "8px 20px",
                  borderRadius: "20px",
                  marginBottom: "20px",
                  border: "1px solid #C7D9FF"
                }}>
                  <span style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#407EF9",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}>
                    Jak to działa
                  </span>
                </div>

                <h2
                  style={{
                    fontSize: "clamp(2.2rem, 4vw, 3rem)",
                    fontWeight: "800",
                    color: "#1f2937",
                    marginBottom: "16px",
                    letterSpacing: "-0.02em",
                    lineHeight: "1.2"
                  }}
                >
                  Cały proces - od rejestracji czasu do rozliczeń
                </h2>

                <p style={{
                  fontSize: "1.1rem",
                  color: "#6b7280",
                  maxWidth: "700px",
                  margin: "0 auto",
                  lineHeight: "1.6"
                }}>
                  Zobacz krok po kroku, jak eGodziny działają w firmach terenowych
                </p>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Features Section */}
      <Container className="py-1">
        {/* Feature 1 */}
        <Row className="align-items-center mb-5 py-5" style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '20px',
          padding: '60px 20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Col md={5} className="order-md-1">
            <div style={{ padding: "0 20px" }}>
              <h3
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "20px",
                  lineHeight: "1.2"
                }}
              >
                Proste raportowanie godzin...
              </h3>

              <p
                style={{
                  fontSize: "1.15rem",
                  color: "#6b7280",
                  marginBottom: "40px",
                  lineHeight: "1.7"
                }}
              >
                Każdy{" "}
                <span id="typing-text" style={{ 
                  color: "#407EF9", 
                  fontWeight: "bold",
                  backgroundColor: "#EEF3FF",
                  padding: "2px 8px",
                  borderRadius: "4px"
                }}>
                  pracownik
                </span>{" "}
                uzupełnia czas pracy w aplikacji, wybierając odpowiedni projekt
                lub zlecenie.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <div style={{
                  backgroundColor: "#FEF2F2",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #EF4444",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#FEE2E2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#DC2626",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✕</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#991B1B", 
                      margin: "0"
                    }}>
                      Problem:
                    </h5>
                  </div>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Zmarnowane dziesiątki godzin na zbieraniu i przepisywaniu papierowych
                        kart czasu pracy.
                      </span>
                    </div>

                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Brakujące i zgubione listy powodują zamęt.
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: "#F0FDF4",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #10B981",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#059669",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✓</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#065F46", 
                      margin: "0"
                    }}>
                      Rozwiązanie:
                    </h5>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Dzięki aplikacji masz pełne zestawienie godzin pracy w kilka
                        kliknięć. Znikają papierowe karty czasu pracy.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6",
                        fontWeight: "600"
                      }}>
                        Oszczędzasz <span style={{ 
                          color: "#059669",
                          fontSize: "1.15rem"
                        }}>14 godzin</span> miesięcznie.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "40px", textAlign: "center" }} className="d-none d-md-block">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#407EF9",
                    fontSize: "1.05rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "none",
                    padding: "10px 20px",
                    transition: "opacity 0.3s ease",
                  }}
                  onClick={() =>
                    window.open(
                      "/register",
                      "_self",
                    )
                  }
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <span>Rozpocznij darmowy okres próbny</span>
                  <span style={{ fontSize: "1.3rem" }}>→</span>
                </div>
              </div>
            </div>
          </Col>

          <Col md={7} className="order-md-2">
            <div style={{ textAlign: "center", padding: "0 20px" }} className="feature-video-wrap">
              <video
                className="rounded-3"
                autoPlay
                loop
                muted
                playsInline
                style={{ 
                  width: "95%", 
                  maxWidth: "95%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)"
                }}
              >
                <source src={deklarowanie_vid} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="d-md-none" style={{ marginTop: "20px", textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#407EF9",
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "none",
                  padding: "10px 20px",
                  transition: "opacity 0.3s ease",
                }}
                onClick={() =>
                  window.open(
                    "/register",
                    "_self",
                  )
                }
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <span>Rozpocznij darmowy okres próbny</span>
                <span style={{ fontSize: "1.3rem" }}>→</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Feature 2 - Rejestracja czasu pracy Start/Stop (GPS) */}
        <Row className="align-items-center mb-5 py-5" style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '20px',
          padding: '60px 20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Col md={7} className="order-2 order-md-1">
            <div style={{ textAlign: "center", padding: "0 20px" }} className="feature-video-wrap">
              <video
                className="rounded-3"
                autoPlay
                loop
                muted
                playsInline
                style={{ 
                  width: "95%", 
                  maxWidth: "95%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)"
                }}
              >
                <source src={rejestrowanie_vid} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </Col>

          <Col md={5} className="order-1 order-md-2">
            <div style={{ padding: "0 20px" }}>
              <h3
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "20px",
                  lineHeight: "1.2"
                }}
              >
                ... lub Rejestracja czasu pracy Start/Stop (GPS)
              </h3>

              <p
                style={{
                  fontSize: "1.15rem",
                  color: "#6b7280",
                  marginBottom: "40px",
                  lineHeight: "1.7"
                }}
              >
                Pracownik rozpoczyna i kończy pracę jednym kliknięciem, a GPS potwierdza jego obecność na miejscu zlecenia.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <div style={{
                  backgroundColor: "#FEF2F2",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #EF4444",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#FEE2E2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#DC2626",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✕</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#991B1B", 
                      margin: "0"
                    }}>
                      Problem:
                    </h5>
                  </div>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Częste rozbieżności między deklarowanymi godzinami a rzeczywistym czasem pracy.
                      </span>
                    </div>

                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Nie ma pewności, kto o której naprawdę pojawił się na projekcie i kiedy zakończył pracę.
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: "#F0FDF4",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #10B981",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#059669",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✓</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#065F46", 
                      margin: "0"
                    }}>
                      Rozwiązanie:
                    </h5>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Masz rzetelny, faktyczny czas pracy. Każde wejście i wyjście jest udokumentowane.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6",
                        fontWeight: "600"
                      }}>
                        Zapominasz o <span style={{ 
                          color: "#059669",
                          fontSize: "1.15rem"
                        }}>rozbieżnościach</span>.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "40px", textAlign: "center" }} className="d-none d-md-block">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#407EF9",
                    fontSize: "1.05rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "none",
                    padding: "10px 20px",
                    transition: "opacity 0.3s ease",
                  }}
                  onClick={() =>
                    window.open(
                      "/register",
                      "_self",
                    )
                  }
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <span>Rozpocznij darmowy okres próbny</span>
                  <span style={{ fontSize: "1.3rem" }}>→</span>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} className="order-3 d-md-none">
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#407EF9",
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "none",
                  padding: "10px 20px",
                  transition: "opacity 0.3s ease",
                }}
                onClick={() =>
                  window.open(
                    "/register",
                    "_self",
                  )
                }
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <span>Rozpocznij darmowy okres próbny</span>
                <span style={{ fontSize: "1.3rem" }}>→</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Feature 3 - Analiza rentowności projektów */}
        <Row className="align-items-center mb-5 py-5" style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '20px',
          padding: '60px 20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Col md={5} className="order-md-1">
            <div style={{ padding: "0 20px" }}>
              <h3
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "20px",
                  lineHeight: "1.2"
                }}
              >
                Analiza rentowności projektów
              </h3>

              <p
                style={{
                  fontSize: "1.15rem",
                  color: "#6b7280",
                  marginBottom: "40px",
                  lineHeight: "1.7"
                }}
              >
                Każdy zaraportowany czas pracy w eGodzinach przypisany jest do
                konkretnego zlecenia lub projektu.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <div style={{
                  backgroundColor: "#FEF2F2",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #EF4444",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#FEE2E2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#DC2626",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✕</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#991B1B", 
                      margin: "0"
                    }}>
                      Problem:
                    </h5>
                  </div>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Nie wiesz, które zadania i projekty zajęły ile czasu.
                      </span>
                    </div>

                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Nie masz pewności na których projektach firma traci, a na których zarabia pieniądze.
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: "#F0FDF4",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #10B981",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#059669",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✓</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#065F46", 
                      margin: "0"
                    }}>
                      Rozwiązanie:
                    </h5>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Znasz koszt roboczogodzin na każdym zleceniu.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Możesz łatwo przeanalizować opłacalność projektów.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Podejmujesz lepsze decyzje oraz dokładniejsze wyceny.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6",
                        fontWeight: "600"
                      }}>
                        Oszczędzasz <span style={{ 
                          color: "#059669",
                          fontSize: "1.15rem"
                        }}>12 godzin</span> miesięcznie.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "40px", textAlign: "center" }} className="d-none d-md-block">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#407EF9",
                    fontSize: "1.05rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "none",
                    padding: "10px 20px",
                    transition: "opacity 0.3s ease",
                  }}
                  onClick={() =>
                    window.open(
                      "/register",
                      "_self",
                    )
                  }
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <span>Rozpocznij darmowy okres próbny</span>
                  <span style={{ fontSize: "1.3rem" }}>→</span>
                </div>
              </div>
            </div>
          </Col>

          <Col md={7} className="order-md-2">
            <div style={{ textAlign: "center", padding: "0 20px" }} className="feature-video-wrap">
              <video
                className="rounded-3"
                autoPlay
                loop
                muted
                playsInline
                style={{ 
                  width: "95%", 
                  maxWidth: "95%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)"
                }}
              >
                <source src={rentownosc_vid} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="d-md-none" style={{ marginTop: "20px", textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#407EF9",
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "none",
                  padding: "10px 20px",
                  transition: "opacity 0.3s ease",
                }}
                onClick={() =>
                  window.open(
                    "/register",
                    "_self",
                  )
                }
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <span>Rozpocznij darmowy okres próbny</span>
                <span style={{ fontSize: "1.3rem" }}>→</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Feature 4 - Podsumowanie dnia */}
        <Row className="align-items-center mb-5 py-5" style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '20px',
          padding: '60px 20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Col md={7} className="order-2 order-md-1">
            <div style={{ textAlign: "center", padding: "0 20px" }} className="feature-video-wrap">
              <video
                className="rounded-3"
                autoPlay
                loop
                muted
                playsInline
                style={{ 
                  width: "95%", 
                  maxWidth: "95%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)"
                }}
              >
                <source src={podsumowanie_dnia_vid} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </Col>

          <Col md={5} className="order-1 order-md-2">
            <div style={{ padding: "0 20px" }}>
              <h3
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "20px",
                  lineHeight: "1.2"
                }}
              >
               Podsumowanie dnia
              </h3>

              <p
                style={{
                  fontSize: "1.15rem",
                  color: "#6b7280",
                  marginBottom: "40px",
                  lineHeight: "1.7"
                }}
              >
                Sprawdź kto nad czym dziś pracował i jak długo.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <div style={{
                  backgroundColor: "#FEF2F2",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #EF4444",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#FEE2E2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#DC2626",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✕</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#991B1B", 
                      margin: "0"
                    }}>
                      Problem:
                    </h5>
                  </div>
                  <div style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}>
                    <span style={{
                      fontSize: "1.05rem",
                      color: "#DC2626",
                      fontWeight: "bold",
                      marginTop: "2px"
                    }}>
                      •
                    </span>
                    <span style={{ 
                      fontSize: "1.05rem", 
                      color: "#7F1D1D",
                      lineHeight: "1.6"
                    }}>
                      Nie wiesz, kto i nad czym dziś pracował.
                    </span>
                  </div>
                </div>

                <div style={{
                  backgroundColor: "#F0FDF4",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #10B981",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#059669",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✓</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#065F46", 
                      margin: "0"
                    }}>
                      Rozwiązanie:
                    </h5>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Gotowe zestawienie godzin pracy każdego dnia. Możesz łatwo porównać pracowników.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Wykrywasz rozbieżności tego samego dnia, a nie pod koniec miesiąca.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6",
                        fontWeight: "600"
                      }}>
                        <span style={{ 
                          color: "#059669",
                          fontSize: "1.15rem"
                        }}>90% mniej nadużyć</span> w deklarowanym czasie pracy.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "40px", textAlign: "center" }} className="d-none d-md-block">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#407EF9",
                    fontSize: "1.05rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "none",
                    padding: "10px 20px",
                    transition: "opacity 0.3s ease",
                  }}
                  onClick={() =>
                    window.open(
                      "/register",
                      "_self",
                    )
                  }
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <span>Rozpocznij darmowy okres próbny</span>
                  <span style={{ fontSize: "1.3rem" }}>→</span>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={12} className="order-3 d-md-none">
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#407EF9",
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "none",
                  padding: "10px 20px",
                  transition: "opacity 0.3s ease",
                }}
                onClick={() =>
                  window.open(
                    "/register",
                    "_self",
                  )
                }
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <span>Rozpocznij darmowy okres próbny</span>
                <span style={{ fontSize: "1.3rem" }}>→</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Feature 5 - Eksport danych i integracje */}
        <Row className="align-items-center mb-5 py-5" style={{
          backgroundColor: '#F9FAFB',
          borderRadius: '20px',
          padding: '60px 20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Col md={5} className="order-md-1">
            <div style={{ padding: "0 20px" }}>
              <h3
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                  color: "#1f2937",
                  marginBottom: "20px",
                  lineHeight: "1.2"
                }}
              >
               Gotowe raporty do <br/> naliczenia płac
              </h3>

              <p
                style={{
                  fontSize: "1.15rem",
                  color: "#6b7280",
                  marginBottom: "40px",
                  lineHeight: "1.7"
                }}
              >
                Dostajesz szereg eksportów w różnych
                formatach oraz możliwych integracji.
              </p>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <div style={{
                  backgroundColor: "#FEF2F2",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #EF4444",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#FEE2E2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#DC2626",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✕</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#991B1B", 
                      margin: "0"
                    }}>
                      Problem:
                    </h5>
                  </div>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Przepisujesz dane z papieru, do excela, a potem do programu
                        kadrowego. 
                      </span>
                    </div>

                    <div style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                    }}>
                      <span style={{
                        fontSize: "1.05rem",
                        color: "#DC2626",
                        fontWeight: "bold",
                        marginTop: "2px"
                      }}>
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#7F1D1D",
                        lineHeight: "1.6"
                      }}>
                        Wypłaty zajmują zdecydowanie zbyt dużo czasu.
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: "#F0FDF4",
                  padding: "24px",
                  borderRadius: "12px",
                  borderLeft: "4px solid #10B981",
                  transition: "transform 0.3s ease",
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "16px"
                  }}>
                    <div style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: "#D1FAE5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#059669",
                      fontSize: "18px",
                      fontWeight: "bold"
                    }}>✓</div>
                    <h5 style={{ 
                      fontSize: "1.2rem", 
                      fontWeight: "700", 
                      color: "#065F46", 
                      margin: "0"
                    }}>
                      Rozwiązanie:
                    </h5>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6"
                      }}>
                        Gotowe raporty do naliczenia płac jednym kliknięciem.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "1.05rem",
                          color: "#059669",
                          fontWeight: "bold",
                          marginTop: "2px"
                        }}
                      >
                        •
                      </span>
                      <span style={{ 
                        fontSize: "1.05rem", 
                        color: "#064E3B",
                        lineHeight: "1.6",
                        fontWeight: "600"
                      }}>
                        Oszczędzasz <span style={{ 
                          color: "#059669",
                          fontSize: "1.15rem"
                        }}>9 godzin</span> miesięcznie.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "40px", textAlign: "center" }} className="d-none d-md-block">
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "#407EF9",
                    fontSize: "1.05rem",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "none",
                    padding: "10px 20px",
                    transition: "opacity 0.3s ease",
                  }}
                  onClick={() =>
                    window.open(
                      "/register",
                      "_self",
                    )
                  }
                  onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
                >
                  <span>Rozpocznij darmowy okres próbny</span>
                  <span style={{ fontSize: "1.3rem" }}>→</span>
                </div>
              </div>
            </div>
          </Col>

          <Col md={7} className="order-md-2">
            <div style={{ textAlign: "center", padding: "0 20px" }} className="feature-video-wrap">
              <video
                className="rounded-3"
                autoPlay
                loop
                muted
                playsInline
                style={{ 
                  width: "95%", 
                  maxWidth: "95%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.35)"
                }}
              >
                <source src={eksporty_vid} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="d-md-none" style={{ marginTop: "20px", textAlign: "center" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#407EF9",
                  fontSize: "1.05rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "none",
                  padding: "10px 20px",
                  transition: "opacity 0.3s ease",
                }}
                onClick={() =>
                  window.open(
                    "/register",
                    "_self",
                  )
                }
                onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"}
                onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
              >
                <span>Rozpocznij darmowy okres próbny</span>
                <span style={{ fontSize: "1.3rem" }}>→</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Testimonial Section */}
        <Row className="mb-3">
          <Col className="text-center">
            <h2
              style={{
                fontSize: "2.2rem",
                fontWeight: "700",
                color: "#1f2937",
                marginTop: "48px",
              }}
            >
              U innych już się sprawdziło
            </h2>
          </Col>
        </Row>

        {/* CASE STUDY REFERENCE */}
        <Row className="mb-3 py-5">
          <Col>
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: '40px',
                maxWidth: '1000px',
                margin: '0 auto',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Row className="align-items-center">
                <Col md={4} className="text-center mb-3 mb-md-0" style={{ paddingRight: '40px' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <Image
                      src={require("../images/staldach-bud-logo.png")}
                      alt="Staldach-Bud Logo"
                      style={{
                        width: '150px',
                        height: 'auto'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '25px', fontSize: '16px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#1f2937' }}>Mariusz Wolny</div>
                    <div style={{ color: '#6b7280', marginBottom: '3px' }}>Prezes Zarządu</div>
                    <div style={{ color: '#6b7280' }}>STALDACH-BUD sp. z o.o.</div>
                  </div>
                </Col>

                <Col md={8}>
                  <blockquote style={{ margin: 0, fontSize: '22px', lineHeight: '1.6' }}>
                    <p style={{
                      fontStyle: 'italic',
                      marginBottom: '25px',
                      color: '#1f2937',
                      fontSize: '24px'
                    }}>
                      "Jako przedsiębiorca wiecznie przytłoczony
                      ilością zadań i obowiązków, nie zdawałem
                      sobie sprawy, że w tak łatwy sposób można
                      poprawić działanie firmy"
                    </p>

                    <div style={{
                      fontSize: '20px',
                      fontWeight: 'bold',
                      marginBottom: '10px',
                      color: '#1f2937'
                    }}>
                      Każdego miesiąca oszczędzamy:
                    </div>

                    <div
                      style={{
                        fontSize: "36px",
                        fontWeight: "bold",
                        color: "#407EF9",
                        marginBottom: "20px",
                      }}
                    >
                      <AnimatedCounter value={9180} suffix=" zł" />
                    </div>

                    <div className="text-end">
                      <Button
                        variant="outline-dark"
                        style={{
                          borderRadius: '10px',
                          padding: '8px 20px',
                          fontWeight: '600',
                          borderWidth: '2px'
                        }}
                        onClick={() => window.open('/case-study', '_blank')}
                      >
                        Przeczytaj więcej
                      </Button>
                    </div>
                  </blockquote>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>

        <Row className="text-center">
          <Col>
            <Button
              className="button-default btn-lg"
              style={{
                whiteSpace: 'nowrap',
                minWidth: 'fit-content'
              }}
              onClick={() =>
                window.open(
                  "/register",
                  "_self",
                )
              }
            >
              Wypróbuj eGodziny
            </Button>
          </Col>
        </Row>
      </Container>

      {/* FAQ Section */}
      <FAQ content={content} />

      <Footer />
    </>
  );
}

export default InzynieriaBudownictwo;
